import { useState } from 'react';
import { useI18n } from '../../i18n';
import { useSEO } from '../../hooks/useSEO';

export default function QRCodeGenerator() {
    const { t } = useI18n();
    const [text, setText] = useState('https://getdev.tools');
    const [size, setSize] = useState(256);
    const [errorLevel, setErrorLevel] = useState<'L' | 'M' | 'Q' | 'H'>('M');

    useSEO({
        title: 'QR Code Generator',
        description: 'Generate QR codes for text, URLs, WiFi credentials, and more',
        keywords: ['qr code', 'generator', 'barcode', 'url', 'wifi', 'qr scanner'],
    });

    const getQRCodeURL = () => {
        // Using a public QR code API service
        const encodedText = encodeURIComponent(text);
        return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodedText}&ecc=${errorLevel}`;
    };

    const downloadQR = () => {
        const link = document.createElement('a');
        link.href = getQRCodeURL();
        link.download = 'qrcode.png';
        link.click();
    };

    const templates = [
        { name: 'URL', value: 'https://example.com' },
        { name: 'Email', value: 'mailto:example@email.com' },
        { name: 'Phone', value: 'tel:+1234567890' },
        { name: 'SMS', value: 'sms:+1234567890' },
        { name: 'WiFi', value: 'WIFI:T:WPA;S:NetworkName;P:Password;;' },
        { name: 'vCard', value: 'BEGIN:VCARD\nVERSION:3.0\nFN:John Doe\nTEL:+1234567890\nEMAIL:john@example.com\nEND:VCARD' },
    ];

    return (
        <div>
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    QR Code Generator
                </h1>
                <p className="text-gray-600">Generate QR codes for URLs, text, WiFi, and more</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Input Section */}
                <div className="space-y-6">
                    {/* Text Input */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Content
                        </label>
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="Enter text, URL, or other data..."
                            className="input-field font-mono text-sm"
                            rows={6}
                        />
                    </div>

                    {/* Templates */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Quick Templates
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {templates.map((template) => (
                                <button
                                    key={template.name}
                                    onClick={() => setText(template.value)}
                                    className="btn-secondary text-sm py-2"
                                >
                                    {template.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Size Control */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Size: {size}x{size} px
                        </label>
                        <input
                            type="range"
                            min="128"
                            max="512"
                            step="32"
                            value={size}
                            onChange={(e) => setSize(Number(e.target.value))}
                            className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>128px</span>
                            <span>512px</span>
                        </div>
                    </div>

                    {/* Error Correction Level */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Error Correction Level
                        </label>
                        <div className="grid grid-cols-4 gap-2">
                            {(['L', 'M', 'Q', 'H'] as const).map((level) => (
                                <button
                                    key={level}
                                    onClick={() => setErrorLevel(level)}
                                    className={`py-2 px-4 rounded-lg border-2 transition-colors ${errorLevel === level
                                            ? 'border-primary-500 bg-primary-50 text-primary-700 font-semibold'
                                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    {level}
                                </button>
                            ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                            L=7%, M=15%, Q=25%, H=30% error recovery
                        </p>
                    </div>

                    {/* Download Button */}
                    <button
                        onClick={downloadQR}
                        disabled={!text}
                        className="btn-primary w-full"
                    >
                        📥 Download QR Code
                    </button>
                </div>

                {/* QR Code Preview */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preview
                    </label>
                    <div className="bg-white border-2 border-gray-200 rounded-lg p-8 flex items-center justify-center min-h-[400px]">
                        {text ? (
                            <img
                                src={getQRCodeURL()}
                                alt="QR Code"
                                className="max-w-full h-auto"
                                style={{ width: size, height: size }}
                            />
                        ) : (
                            <p className="text-gray-400 text-center">
                                Enter content to generate QR code
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Info Sections */}
            <div className="mt-6 space-y-4">
                {/* WiFi Format */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-2">
                        📶 WiFi QR Code Format
                    </h3>
                    <code className="text-sm text-blue-800 bg-white px-2 py-1 rounded block">
                        WIFI:T:WPA;S:NetworkName;P:Password;;
                    </code>
                    <p className="text-xs text-blue-700 mt-2">
                        T = Security type (WPA, WEP, or nopass), S = SSID, P = Password
                    </p>
                </div>

                {/* vCard Format */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="font-semibold text-green-900 mb-2">
                        👤 vCard Contact Format
                    </h3>
                    <code className="text-sm text-green-800 bg-white px-2 py-1 rounded block whitespace-pre">
                        {`BEGIN:VCARD
VERSION:3.0
FN:Full Name
TEL:+1234567890
EMAIL:email@example.com
END:VCARD`}
                    </code>
                </div>

                {/* Use Cases */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                    <h3 className="font-semibold text-primary-900 mb-2">
                        💡 Common Use Cases
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-primary-800">
                        <div className="flex items-start space-x-2">
                            <span>🔗</span>
                            <span>Share website URLs</span>
                        </div>
                        <div className="flex items-start space-x-2">
                            <span>📱</span>
                            <span>App download links</span>
                        </div>
                        <div className="flex items-start space-x-2">
                            <span>📶</span>
                            <span>WiFi credentials</span>
                        </div>
                        <div className="flex items-start space-x-2">
                            <span>👤</span>
                            <span>Contact information</span>
                        </div>
                        <div className="flex items-start space-x-2">
                            <span>✉️</span>
                            <span>Email addresses</span>
                        </div>
                        <div className="flex items-start space-x-2">
                            <span>📍</span>
                            <span>Location coordinates</span>
                        </div>
                    </div>
                </div>

                {/* Privacy Notice */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h3 className="font-semibold text-yellow-900 mb-2">
                        ⚠️ Privacy Notice
                    </h3>
                    <p className="text-sm text-yellow-800">
                        This tool uses an external API (qrserver.com) to generate QR codes.
                        Your data is sent to their server. For sensitive information, consider using
                        an offline QR code generator.
                    </p>
                </div>
            </div>
        </div>
    );
}
